// ---------------------------------------------------------------------------
// Platform Icons -- Shared V11 Icons for Social Media Platforms
// ---------------------------------------------------------------------------
// Reuses the same icons from V9 for consistency across the application.
// ---------------------------------------------------------------------------

import React from 'react'
import { Instagram, Youtube, Music2 } from 'lucide-react'

// ---------------------------------------------------------------------------
// Custom X Icon (same as V9)
// ---------------------------------------------------------------------------

export const XIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
)

// ---------------------------------------------------------------------------
// Platform Configuration
// ---------------------------------------------------------------------------

export interface PlatformIconConfig {
  readonly id: string
  readonly name: string
  readonly icon: React.ReactNode
  readonly color: string
  readonly initials: string
}

/**
 * Resolves a CSS custom property to its computed hex value.
 * Falls back to the provided fallback if CSS vars are unavailable (e.g., SSR/tests).
 */
function resolveCssVar(varName: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback
  const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim()
  return value || fallback
}

/** Platform color fallbacks (match index.css --platform-* vars) */
export const PLATFORM_FALLBACKS: Readonly<Record<string, string>> = {
  instagram: '#D62976',
  x: '#1A8CD8',
  tiktok: '#00D1C1',
  youtube: '#CD201F',
} as const

export const PLATFORM_ICONS: Record<string, PlatformIconConfig> = {
  Instagram: {
    id: 'instagram',
    name: 'Instagram',
    icon: <Instagram className="h-4 w-4" />,
    get color() { return resolveCssVar('--platform-instagram', PLATFORM_FALLBACKS.instagram) },
    initials: 'IG',
  },
  TikTok: {
    id: 'tiktok',
    name: 'TikTok',
    icon: <Music2 className="h-4 w-4" />,
    get color() { return resolveCssVar('--platform-tiktok', PLATFORM_FALLBACKS.tiktok) },
    initials: 'TT',
  },
  YouTube: {
    id: 'youtube',
    name: 'YouTube',
    icon: <Youtube className="h-4 w-4" />,
    get color() { return resolveCssVar('--platform-youtube', PLATFORM_FALLBACKS.youtube) },
    initials: 'YT',
  },
  X: {
    id: 'x',
    name: 'X',
    icon: <XIcon className="h-4 w-4" />,
    get color() { return resolveCssVar('--platform-x', PLATFORM_FALLBACKS.x) },
    initials: 'X',
  },
}

// Helper to get platform icon by name (case-insensitive)
export function getPlatformIcon(platformName: string | null | undefined): PlatformIconConfig | null {
  if (!platformName) return null
  const normalized = platformName.toLowerCase()

  if (normalized === 'instagram' || normalized === 'ig') {
    return PLATFORM_ICONS.Instagram
  }
  if (normalized === 'tiktok' || normalized === 'tt') {
    return PLATFORM_ICONS.TikTok
  }
  if (normalized === 'youtube' || normalized === 'yt') {
    return PLATFORM_ICONS.YouTube
  }
  if (normalized === 'x' || normalized === 'twitter') {
    return PLATFORM_ICONS.X
  }

  return null
}
